import { expandTreePath, toggleTreePath, type TreePath } from '@st/util/tree-expansion'
import {
  deliverReturn,
  deliveryWizardModule,
  initialDeliveryWizard,
  selDeliveryDraft,
  type DeliveryWizardMessage
} from './document-viewer-extensions/tax-return-delivery/delivery-wizard'
import {
  finishOrganizerWrite,
  organizerDraft,
  closesAfterSave,
  reopenOrganizerDraft,
  startOrganizerWrite,
  type OrganizerWriteFinished
} from './st-send-client-organizer-module'
import { folderInvoice, selFieldsSidebarShown } from './st-folder-selectors'
import { setInvoiceRequest } from './document-viewer-extensions/tax-return-delivery/invoice-link'
import { checklistModule, type ChecklistMessage } from '~/features/checklists/module'
import type { Range, STChecklistItem, TaxReturnUnit } from '@st/sdk'
import {
  selBundleEditing,
  reduceBundleSelection
} from './document-viewer-extensions/bundled-document/selection'
import {
  child,
  defModule,
  Task,
  transitions,
  Update,
  type TransitionRow,
  type UpdateResult,
  type TaskDeclaration,
  type TaskMessage
} from '@st/redux'
import { deepEqual } from '@st/util/deep-equal'
import type { JSONValue } from '@st/util/json-value'
import {
  PrepareDocumentDownload,
  Provider,
  ReportDocumentIssue,
  STDocument,
  STOpenFolderState,
  UnlockClientOrganizer
} from '@st/sdk'
import type {
  DocumentAnnotation,
  HttpStatusError,
  MarkQuestionnaireAsCompleted,
  SDKResponseFor,
  TimeoutError,
  TransportError
} from '@st/sdk'
import { isHttpStatusError, isTimeoutError, isTransportError } from '@st/sdk'
import type { FormConfig } from '@st/ui-config'
import { Duration, Effect, Layer, Stream } from 'effect'
import { match, P } from '@st/pattern'
import type { ViewerNavigation, ViewerPresentation, ViewerSelection } from '@st/document-viewer-kit'
import {
  Analytics,
  BrowserCookies,
  BrowserDownloads,
  ErrorReporting,
  FormConfigRepo,
  RequestSchedule,
  RequestScheduler,
  RequestSchedulerEvent,
  type RequestFailureHandling,
  Routing,
  RuntimeConfig,
  SdkClient,
  Snackbar,
  type SdkFailure,
  observeRequestScheduler,
  query
} from '~/effects'
import {
  callSdkRequest,
  observeCurrentUser,
  observeRoute,
  runSdkRequest
} from '~/features/module-runtime'
import { copyTextToClipboard } from '~/features/copy-to-clipboard'
import type { AppRoute } from '~/features/routes'
import {
  FolderQuestionnaireCommand,
  FolderQuestionnaireMessage,
  QuestionnaireCommandMessage,
  type FolderQuestionnaireUpdate,
  initFolderQuestionnaireState,
  reconcileFolderQuestionnaire,
  reduceFolderQuestionnaire,
  selQuestionnaireAutosave
} from './st-folder-questionnaire'
import {
  buildGrantCookieAssignment,
  hasFolderGrantInCookie
} from '../st-questionnaire/client-mfa-gate-logic'
import { getDocumentSections, selChecklistItems, selUploadedDocuments } from './st-folder-selectors'
import { formatFolderEntityName } from './st-folder-display'
import {
  stFolderDocViewerLayer,
  stFormationChecklistDocViewerModule,
  stFormationDocViewerModule,
  stPdfDocViewerModule,
  type STDocViewerDocument,
  type STDocViewerRuntimeMessage
} from '~/st-doc-viewer/runtime-definition'
import {
  resolveChecklistViewerPages,
  resolveViewerDocKind,
  type ViewerDocKind
} from '~/st-doc-viewer/viewer-document-model'
import {
  resolveDocumentDataFieldAnnotations,
  isCommentAnnotation,
  resolveViewerCommentAnnotations,
  toBoundedCommentAnnotation,
  type STViewerAnnotation
} from '~/st-doc-viewer/viewer-annotations'
import type { DocumentViewerCommand } from '@st/document-viewer-kit'
import {
  ANNOTATIONS_PLUGIN_ID,
  selActiveAnnotation,
  selAnnotationsPluginState,
  toAnnotationsPluginMessage,
  type AnnotationsPluginCommand
} from '@st/document-viewer-kit/plugins/annotations'
import { navigateToChecklist, resolveDocumentLocationForTarget } from './st-folder-focus'
import { selFormationLayout } from './st-formation-selectors'
import type { JSONSchema } from '~/st-doc-viewer/schema-document-data'
import type { SidebarVisibility } from '~/st-doc-viewer/sidebar-layout'
import { contractDraftFieldAnnotations } from '~/features/contract-editing/fields'
import {
  taxReturnDeliveryTasks,
  requestDeliveryDeparture,
  taxReturnDeliveryDocumentState,
  initTaxReturnDeliveryState,
  reconcileTaxReturnDeliveryState,
  selTaxReturnDeliveryDirty,
  selTaxReturnDeliveryPolicy,
  selTaxReturnDeliverySaving,
  updateTaxReturnDelivery,
  type TaxReturnDeliveryMessage
} from './document-viewer-extensions/tax-return-delivery/state'
import { contractDraftFieldChangeIds } from '~/features/contract-editing/fields'
import {
  emptyRequestQueue,
  enqueueRequest as enqueueRequestLedger,
  finalizeSettledQueueByVsn as finalizeSettledQueueByVsnLedger,
  markRequestFailed as markRequestFailedLedger,
  markRequestStarted as markRequestStartedLedger,
  markRequestSettledAtVsn as markRequestSettledAtVsnLedger,
  selectQueuedRequestById
} from './st-folder-request-queue'
import type {
  FolderFormationConfigState,
  FolderRequest,
  FolderViewMode,
  FolderNavigation,
  NavigationRequest,
  OpenFolderState,
  AuthorizedOpenFolderState,
  UnauthorizedOpenFolderState,
  STFolderModuleState,
  STFolderReadyState,
  TaxReturnPullStatus,
  ViewerScope
} from './st-folder-types'
import {
  initBulkUploadState,
  reduceBulkUpload,
  resolveBulkUpload as resolveBulkUploadProgram,
  type BulkUploadMessage
} from './bulk-upload/bulk-upload-workflow'

/** What the folder shows in which viewer; the viewer reconciles it atomically. */
type ViewerProjection = {
  scope: ViewerScope
  presentation: ViewerPresentation<STDocViewerDocument>
}

const CLOSED_VIEWER_PRESENTATION: ViewerPresentation<STDocViewerDocument> = {
  document: null,
  annotations: {},
  navigation: null,
  initialZoom: undefined
}

const checklistChild = child(checklistModule, 'checklist')
const deliveryWizardChild = child(deliveryWizardModule, 'deliveryWizard')

const viewerModules = {
  'pdf': stPdfDocViewerModule,
  'formation': stFormationDocViewerModule,
  'formation-checklist': stFormationChecklistDocViewerModule
} as const

// These implementations share one state slot. Their Task scopes include the
// implementation so switching viewers closes the old viewer's work.
const pdfViewerChild = child(stPdfDocViewerModule, 'viewer', ['viewer:pdf'])
const viewerChildren: Record<ViewerScope, typeof pdfViewerChild> = {
  'pdf': pdfViewerChild,
  'formation': child(stFormationDocViewerModule, 'viewer', ['viewer:formation']),
  'formation-checklist': child(stFormationChecklistDocViewerModule, 'viewer', [
    'viewer:formation-checklist'
  ])
}

type STFolderLoadingState = Extract<STFolderModuleState, { status: 'loading' }>
type STFolderUnauthorizedState = Extract<STFolderModuleState, { status: 'unauthorized' }>
export type {
  BulkUploadDialogState,
  BulkUploadEntries,
  FolderRequest,
  NavigationRequest,
  STFolderModuleState,
  STFolderReadyState
} from './st-folder-types'

const observeFolderRoute = observeRoute({
  name: P.union(
    'organization_folder',
    'organization_folder_debug',
    'organization_preview_questionnaire',
    'client_folder'
  )
})

const resolveBulkUpload = Task.define((uploadId: string) =>
  resolveBulkUploadProgram(uploadId).pipe(
    Stream.map((message) => ({ type: 'bulkUpload', message }) as const)
  )
)

const loadFormationConfig = Task.define((schemaId: string) =>
  Effect.gen(function* () {
    const formConfigRepo = yield* FormConfigRepo
    const config = yield* formConfigRepo.get(schemaId)
    if (!config) {
      return yield* Effect.die(new Error(`Missing form config for ${schemaId}`))
    }
    return { type: 'formationConfigLoaded', config } as const
  })
)

const guardDeliveryNavigation = Task.define(() =>
  Effect.gen(function* () {
    const routing = yield* Routing
    return yield* routing.guardNavigation({
      message: 'Discard unsaved changes and leave this page?'
    })
  })
)

function deliveryNavigationTasks(state: STFolderModuleState): STFolderSubscription[] {
  if (!selTaxReturnDeliveryDirty(state)) return []
  return [guardDeliveryNavigation()]
}

const readQuestionnaireMfaGrant = Task.define((folderId: string) =>
  Effect.gen(function* () {
    const cookies = yield* BrowserCookies
    const cookieHeader = yield* cookies.read
    return {
      type: 'questionnaire',
      message: {
        type: 'mfaGrantStatusLoaded',
        hasGrant: hasFolderGrantInCookie(cookieHeader, folderId)
      }
    } as const
  })
)

const validateQuestionnaireSubmit = Task.define((folderId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* callSdkRequest(sdk.send, {
      type: 'folders/validateSubmitClientOrganizer',
      folderId
    })

    if (result.type == 'ok') {
      return {
        type: 'questionnaire',
        message: {
          type: 'submitValidationReceived',
          validationResult: result.response
        }
      } as const
    }

    return [
      {
        type: 'questionnaire',
        message: { type: 'submitValidationFailed' }
      },
      { type: 'showErrorSnackbar', message: 'Something went wrong. Please try again.' }
    ] as const
  })
)

const waitForQuestionnaireAutosave = Task.define((pageId: string, progress: number) =>
  Effect.sleep('5 seconds').pipe(
    Effect.as({
      type: 'questionnaire',
      message: { type: 'autosaveFired', pageId, progress }
    } as const)
  )
)

const loadViewerConfig = Task.define(() =>
  Effect.gen(function* () {
    const { pushTaxReturn } = yield* RuntimeConfig
    return { type: 'viewerConfigLoaded', pushTaxReturn } as const
  })
)

export type STFolderMessage =
  | { type: 'checklist'; message: ChecklistMessage }
  | TaskMessage<typeof loadViewerConfig>
  | TaskMessage<typeof observeCurrentUser>
  | TaskMessage<typeof observeFolderRoute>
  | { type: 'bulkUpload'; message: BulkUploadMessage }
  | {
      type: 'stateLoad'
      vsn: number
      state: OpenFolderState
    }
  | { type: 'navigationRequested'; request: NavigationRequest }
  | { type: 'formationConfigLoaded'; config: FormConfig }
  | { type: 'bundleExtractionRequested'; ranges: Range[] }
  | { type: 'bundleSelectionToggled'; range: Range }
  | { type: 'sidebarVisibilityChanged'; visibility: SidebarVisibility }
  | { type: 'treeExpansionToggled'; path: TreePath }
  | { type: 'fieldsSidebarToggled' }
  | { type: 'taxReturnDeliverySummaryOpened' }
  | { type: 'taxReturnDelivery'; message: TaxReturnDeliveryMessage }
  | { type: 'formationFieldChanged'; key: string; value: JSONValue }
  | { type: 'requestEnqueued'; request: FolderRequest }
  | ({ type: 'downloadDocument' } & Omit<PrepareDocumentDownload, 'type' | 'folderId'>)
  | { type: 'copyWorkpaperLink'; url: string }
  | { type: 'copyText'; text: string }
  | { type: 'unlockClientOrganizer'; request: UnlockClientOrganizer }
  | { type: 'updateFolderTags'; addTagIds: string[]; removeTagIds: string[] }
  | { type: 'reportDocumentIssue'; request: ReportDocumentIssue; documentName: string }
  | { type: 'deleteAllDocuments' }
  | { type: 'previewQuestionnaire' }
  | { type: 'openUploadDeleteDialog'; doc: STDocument }
  | { type: 'openUploadReasonDialog'; checklistItem: STChecklistItem }
  | { type: 'closeUploadDialog' }
  | { type: 'uploadReasonChanged'; reason: string | undefined }
  | { type: 'saveUploadReason' }
  | { type: 'removeUploadReason' }
  | { type: 'confirmUploadDelete' }
  | { type: 'paymentLinkOpened' }
  | { type: 'paymentLinkClosed' }
  | { type: 'paymentLinkEdited'; edit: { url: string } | { required: boolean } }
  | { type: 'paymentLinkSaved' }
  | { type: 'paymentLinkRemoved' }
  | { type: 'deliveryRequested'; contractId: string; emailsManagedBy: 'stanfordtax' | 'karbon' }
  | { type: 'deliveryWizard'; message: DeliveryWizardMessage }
  | OrganizerWriteFinished
  // Close the delivery wizard's confirmation and open the tax payment checklist.
  | { type: 'deliveryChecklistRequested' }
  | { type: 'karbonDeliveryClosed' }
  | { type: 'karbonLinkCopyRequested'; url: string }
  | { type: 'markQuestionnaireAsCompleted'; request: MarkQuestionnaireAsCompleted }
  | { type: 'markDocumentAsRead'; folderId: string; documentId: string }
  | {
      type: 'deleteDocument'
      folderId: string
      documentId: string
      documentName: string
    }
  | { type: 'showSnackbar'; message: string }
  | { type: 'showErrorSnackbar'; message: string }
  | { type: 'showTaxReturnPullStatusDialog'; status: TaxReturnPullStatus }
  | { type: 'showTaxReturnPullErrorDialog'; message: string }
  | { type: 'closeTaxReturnPullStatusDialog' }
  | { type: 'unlockClientOrganizerSucceeded' }
  | { type: 'refreshTaxReturn' }
  | {
      type: 'pullInitialTaxReturnAndLink'
      provider: Provider
      providerId: string
    }
  | {
      type: 'renameDocument'
      folderId: string
      documentId: string
      oldDocumentName: string
      newDocumentName: string
    }
  | {
      type: 'moveDocument'
      folderId: string
      documentId: string
      documentName: string
      documentTypeId: string
      destinationLabel: string
    }
  | {
      type: 'linkDocumentTaxReturnUnit'
      folderId: string
      documentId: string
      documentName: string
      taxReturnUnitPath: TaxReturnUnit['path']
    }
  | {
      type: 'questionnaire'
      message: FolderQuestionnaireMessage | QuestionnaireCommandMessage
    }
  | { type: 'viewer'; message: STDocViewerRuntimeMessage }
  | SDKRequestStarted
  | SDKResponse
  | SDKRequestUnauthorized
  | SDKRequestFailed
  | { type: 'settleGraceElapsed'; itemId: string }

type SDKRequestStarted = {
  type: 'requestStarted'
  requestId: string
}

type SDKResponse = {
  type: 'requestSucceeded'
  requestId: string
  queuedOnSuccessMessage?: STFolderMessage | STFolderMessage[] | null
}

type SDKRequestUnauthorized = {
  type: 'requestUnauthorized'
  requestId: string
  handling: RequestFailureHandling<STFolderMessage | STFolderMessage[]>
}

type SDKRequestFailed = {
  type: 'requestFailed'
  requestId: string
  handling: RequestFailureHandling<STFolderMessage | STFolderMessage[]>
}

/**
 * Queue runtime messages describe scheduler/optimistic-queue lifecycle, not the
 * canonical folder state lifecycle.
 *
 * Common request flow:
 * 1. requestEnqueued creates a queue item
 * 2. requestStarted means the scheduler began the RPC
 * 3. requestSucceeded/requestFailed means the RPC finished
 * 4. queuedOnSuccessMessage/queuedOnFailedMessage is the domain follow-up for
 *    that RPC result and should be handled immediately
 * 5. stateLoad may later advance folderStateVsn and finalize the
 *    settled optimistic item
 *
 * settleGraceElapsed is only a delayed pulse associated with a settled request.
 * It is not the authoritative state transition that finalizes the queue item.
 */
type QueueRuntimeMessage = Extract<
  STFolderMessage,
  {
    type:
      | 'requestStarted'
      | 'requestSucceeded'
      | 'requestUnauthorized'
      | 'requestFailed'
      | 'settleGraceElapsed'
  }
>
type FormationConfigMessage = Extract<STFolderMessage, { type: 'formationConfigLoaded' }>
type FolderContext = {
  folderId: string
  viewMode: FolderViewMode
}
type RouteToFolderContext = { type: 'no_folder' } | { type: 'folder'; context: FolderContext }
type STFolderUpdate = UpdateResult<STFolderModuleState, STFolderMessage>
type STFolderReadyUpdate = UpdateResult<STFolderReadyState, STFolderMessage>
type STFolderSubscription = TaskDeclaration<STFolderMessage>

function mapQuestionnaireUpdate(
  state: STFolderReadyState,
  update: FolderQuestionnaireUpdate
): STFolderReadyUpdate {
  return Update.map(update, {
    state: (questionnaire) => ({ ...state, questionnaire }),
    message: (message) => ({ type: 'questionnaire', message })
  })
}
type STFolderSchedulerEvent = RequestSchedulerEvent<
  unknown,
  SdkFailure,
  STFolderMessage | STFolderMessage[] | null
>

const observeFolderRequestScheduler = Task.define(() =>
  Task.pipe(
    observeRequestScheduler<unknown, SdkFailure, STFolderMessage | STFolderMessage[] | null>(),
    Task.map(schedulerEventToMessage)
  )
)

const observeFolderTopic = Task.define((currentUserId: string, folderId: string) =>
  query({ type: 'folders/getOpenFolderState', folderId }).pipe(
    Stream.zipWithIndex,
    Stream.map(([state, index]): STFolderMessage => ({ type: 'stateLoad', state, vsn: index + 1 })),
    Stream.orDie
  )
)

function resolveViewerScopeFromDocKind(docKind: ViewerDocKind): ViewerScope {
  if (docKind == 'formation') {
    return 'formation'
  }
  if (docKind == 'formation-checklist') {
    return 'formation-checklist'
  }
  return 'pdf'
}

function resolveViewerModule(scope: ViewerScope) {
  return viewerModules[scope]
}

function resolveViewerChild(scope: ViewerScope) {
  return viewerChildren[scope]
}

/**
 * Each scope is a separate viewer module, so switching starts a fresh viewer
 * with no navigation record. That cannot replay a settled intent: the scope
 * follows the selected document, which only changes with a new navigation
 * token (or with none, when a folder loads).
 */
function switchViewerScope<T extends STFolderModuleState>(
  state: T,
  scope: ViewerScope
): UpdateResult<T, STFolderMessage> {
  if (state.viewerScope == scope) {
    return state
  }
  return Update.pipe(
    discardDeliveryFieldDrafts(state),
    (closed) =>
      routeViewerChild(closed, closed.viewerScope, {
        type: 'present',
        presentation: CLOSED_VIEWER_PRESENTATION
      }),
    (closed) =>
      Update.mapState(
        resolveViewerChild(scope).init(undefined),
        (viewer): T => ({ ...closed, viewerScope: scope, viewer })
      )
  )
}

// TRD field drafts belong to an explicit-save editor, unlike ordinary comment
// drafts. Close them before the viewer flushes its session. Source refreshes
// within the same document keep the overlay; confirmed departures do not.
function discardDeliveryFieldDrafts<T extends STFolderModuleState>(
  state: T
): UpdateResult<T, STFolderMessage> {
  if (state.viewerScope != 'pdf') return state
  let update: UpdateResult<T, STFolderMessage> = state
  for (const annotationId of contractDraftFieldChangeIds(state.viewer)) {
    update = Update.pipe(update, (next) =>
      routeViewerChild(next, next.viewerScope, {
        type: 'viewer',
        message: toAnnotationsPluginMessage({ type: 'discardAnnotationDraft', annotationId })
      })
    )
  }
  return update
}

function activeViewerActivities(state: STFolderModuleState): STFolderSubscription[] {
  return [...resolveViewerChild(state.viewerScope).tasks(state.viewer)]
}

function routeViewerChild<T extends STFolderModuleState>(
  state: T,
  scope: ViewerScope,
  message: STDocViewerRuntimeMessage
): UpdateResult<T, STFolderMessage> {
  return resolveViewerChild(scope).update(state.viewer, message, (viewer) => ({ ...state, viewer }))
}

function routeViewerStateByScope(
  state: STFolderModuleState,
  scope: ViewerScope,
  message: STDocViewerRuntimeMessage
): STFolderUpdate {
  const viewerCommand = message.type == 'viewerCommand' ? message.command : null

  return Update.pipe(
    state,
    (next) => {
      if (
        message.type == 'present' &&
        next.viewer.document?.sessionKey != message.presentation.document?.sessionKey
      ) {
        return discardDeliveryFieldDrafts(next)
      }
      return next
    },
    (next) => switchViewerScope(next, scope),
    (next) => routeViewerChild(next, scope, message),
    (nextState) =>
      viewerCommand == null ? nextState : handleViewerCommand(nextState, viewerCommand)
  )
}

function routeReadyViewerStateByScope(
  state: STFolderReadyState,
  scope: ViewerScope,
  message: STDocViewerRuntimeMessage
): STFolderReadyUpdate {
  return routeViewerStateByScope(state, scope, message) as STFolderReadyUpdate
}

export const stFolderModule = defModule<{ state: STFolderModuleState; message: STFolderMessage }>({
  layer: stFolderDocViewerLayer,
  init: () => {
    const viewerScope = 'pdf' as const
    const viewerInit = resolveViewerChild(viewerScope).init(undefined)

    const baseState = {
      status: 'idle',
      deliveryWizard: initialDeliveryWizard,
      organizerWrites: {},
      nextRequestId: 1,
      currentUserId: null,
      pushTaxReturn: false,
      viewerScope
    } as const

    return Update.mapState(viewerInit, (viewer) => ({
      ...baseState,
      viewer
    }))
  },
  tasks: (state) => [
    loadViewerConfig(),
    observeFolderRoute(),
    observeCurrentUser(),
    ...deliveryWizardChild.tasks(state.deliveryWizard),
    ...folderTopicTasks(state),
    ...requestQueueSubscriptions(state),
    ...activeViewerActivities(state),
    ...formationConfigTasks(state),
    ...questionnaireTasks(state),
    ...deliveryNavigationTasks(state),
    ...taxReturnDeliveryTasks(state),
    ...bulkUploadTasks(state),
    ...match(state)
      .with({ status: 'ready' }, (ready) => checklistChild.tasks(ready.checklist))
      .otherwise(() => [])
  ],
  update: (state, message) => Update.pipe(updateFolder(state, message), reconcileReadyState(state))
})

function reconcileReadyState(
  previousState: STFolderModuleState
): (state: STFolderModuleState) => STFolderUpdate {
  return (state) => {
    if (state.status != 'ready') {
      return state
    }

    // Leaving the checklist discards its draft just as unmounting its old local editor did.
    const nextState = match(state)
      .with(
        P._,
        (state) => state.selectedDocId != 'checklist' && state.checklist.dialog != null,
        (state) =>
          checklistChild.update(state.checklist, { type: 'closeChecklistDialog' }, (checklist) => ({
            ...state,
            checklist
          }))
      )
      .otherwise(() => state)
    return Update.pipe(
      nextState,
      reconcileTaxReturnDeliveryState,
      (readyState) => reconcileViewerPhase(previousState, readyState),
      (readyState) => reportingPhase(previousState, readyState)
    )
  }
}

/**
 * The folder's transitions. Rows every phase shares come first; a message a
 * phase does not list is impossible there and throws.
 */
const everyPhase = {
  deliveryWizard: (state, { message }) => updateDeliveryWizard(state, message),
  // The delivery's writes outlive the wizard and the binder that started them;
  // each outcome is recorded under its draft. A sent delivery shows its
  // confirmation; a save the wizard was waiting on closes it.
  organizerWriteFinished: (state, finished) => {
    const next = {
      ...state,
      organizerWrites: finishOrganizerWrite(state.organizerWrites, finished)
    }
    const closes =
      selDeliveryDraft(state.deliveryWizard) == finished.draft &&
      closesAfterSave(state.organizerWrites[finished.draft], finished)
    return closes ? { ...next, deliveryWizard: initialDeliveryWizard } : next
  },
  viewerConfigLoaded: (state, message) => ({ ...state, pushTaxReturn: message.pushTaxReturn }),
  currentUserChanged: (state, message) => ({ ...state, currentUserId: message.currentUserId }),
  routeChanged: (state, message) => reduceRouteChanged(state, message.route),
  viewer: (state, message) => handleViewerMessage(state, message.message)
} satisfies TransitionRow<STFolderModuleState, STFolderMessage, STFolderUpdate>

const updateFolder = transitions<STFolderModuleState, STFolderMessage, 'status', STFolderUpdate>(
  'status',
  {
    // As before the table, a folder that has gone idle ignores late lifecycle messages.
    idle: {
      ...everyPhase,
      stateLoad: (state) => state,
      formationConfigLoaded: (state) => state,
      navigationRequested: (state) => state
    },
    loading: {
      ...everyPhase,
      stateLoad: (state, message) =>
        match(message.state)
          .with({ access: 'authorized' }, (folderState) =>
            advanceToReady(state, folderState, message.vsn)
          )
          .with({ access: 'unauthorized' }, (folderState) =>
            advanceToUnauthorized(state, folderState, message.vsn)
          )
          .exhaustive(),
      // Only the latest destination matters while documents are unavailable.
      navigationRequested: (state, message) => ({ ...state, deferredNavigation: message.request })
    },
    unauthorized: {
      ...everyPhase,
      stateLoad: (state, message) =>
        match(message.state)
          .with({ access: 'authorized' }, (folderState) =>
            advanceToReady(restartFolderLoading(state), folderState, message.vsn)
          )
          .with({ access: 'unauthorized' }, (folderState) =>
            advanceToUnauthorized(state, folderState, message.vsn)
          )
          .exhaustive()
    },
    ready: {
      ...everyPhase,
      // Folder lifecycle
      formationConfigLoaded: (readyState, message) => {
        const nextState = reduceFormationConfigState(readyState, message)
        return mapQuestionnaireUpdate(
          nextState,
          reconcileFolderQuestionnaire({
            state: nextState.questionnaire,
            stFolder: nextState
          })
        )
      },
      stateLoad: (readyState, message) => {
        if (message.state.access == 'unauthorized') {
          return advanceToUnauthorized(readyState, message.state, message.vsn)
        }
        const loadedState: STFolderReadyState = {
          ...readyState,
          status: 'ready',
          folderState: message.state,
          folderStateVsn: message.vsn
        }
        const nextState: STFolderReadyState = {
          ...loadedState,
          requestQueue: finalizeSettledQueueByVsnLedger(loadedState.requestQueue, message.vsn)
        }
        return mapQuestionnaireUpdate(
          nextState,
          reconcileFolderQuestionnaire({
            state: nextState.questionnaire,
            stFolder: nextState
          })
        )
      },
      navigationRequested: (readyState, message) =>
        applyNavigationRequest(readyState, message.request),
      checklist: (readyState, message) =>
        match(message.message)
          .with({ type: 'persist' }, ({ requests }) =>
            requests.reduce<STFolderReadyUpdate>(
              (update, request) =>
                Update.pipe(update, (state) =>
                  match(request)
                    .with({ type: 'folders/deleteChecklistItem' }, (request) => {
                      const item = state.folderState.checklistItems.find(
                        (item) => item.id == request.checklistItemId
                      )!
                      return callSdk(state, request, {
                        onSuccess: () => ({
                          type: 'showSnackbar',
                          message: `Deleted checklist item ${item.name}`
                        })
                      })
                    })
                    .otherwise((request) => callSdk(state, request))
                ),
              readyState
            )
          )
          .otherwise((intent) =>
            checklistChild.update(readyState.checklist, intent, (checklist) => ({
              ...readyState,
              checklist
            }))
          ),
      // Questionnaire domain
      questionnaire: (readyState, message) => {
        const questionnaireMessage = message.message
        if (questionnaireMessage.type == 'questionnaireCommand') {
          return runQuestionnaireCommand(readyState, questionnaireMessage.command)
        }

        return mapQuestionnaireUpdate(
          readyState,
          reduceFolderQuestionnaire({
            state: readyState.questionnaire,
            message: questionnaireMessage,
            stFolder: readyState
          })
        )
      },
      // Queue runtime events
      requestStarted: advanceQueueRuntime,
      requestSucceeded: advanceQueueRuntime,
      requestUnauthorized: advanceQueueRuntime,
      requestFailed: advanceQueueRuntime,
      settleGraceElapsed: advanceQueueRuntime,
      bulkUpload: (readyState, message) => ({
        ...readyState,
        bulkUpload: reduceBulkUpload({
          state: readyState.bulkUpload,
          message: message.message,
          existingDocuments: readyState.folderState.documents
        })
      }),
      bundleExtractionRequested: (readyState, message) =>
        callSdk(
          { ...readyState, bundleSelection: message.ranges },
          {
            type: 'folders/splitBundledDocument',
            folderId: readyState.folderId,
            sourceDocumentId: readyState.selectedDocId!,
            ranges: message.ranges
          }
        ),
      bundleSelectionToggled: (readyState, message) => {
        const view = selBundleEditing(readyState)
        return {
          ...readyState,
          bundleSelection: reduceBundleSelection(
            view.selectedRanges,
            { type: 'selectionToggled', range: message.range },
            view.groups
          )
        }
      },
      treeExpansionToggled: (readyState, message) => ({
        ...readyState,
        expandedPaths: toggleTreePath(readyState.expandedPaths, message.path)
      }),
      sidebarVisibilityChanged: (readyState, message) => ({
        ...readyState,
        sidebarVisibility: message.visibility
      }),
      fieldsSidebarToggled: (readyState) =>
        match(selFieldsSidebarShown(readyState))
          .with(true, () => selectAnnotationTool(readyState, 'select'))
          .with(false, () =>
            selectAnnotationTool({ ...readyState, sidebarVisibility: 'expanded' }, 'field')
          )
          .exhaustive(),
      // Opens rather than toggles; the tab switch keeps its unsaved-edit guard.
      taxReturnDeliverySummaryOpened: (readyState) =>
        reduceTaxReturnDelivery(
          { ...readyState, sidebarVisibility: 'expanded' },
          { type: 'tabSelected', tab: 'summary' }
        ),
      taxReturnDelivery: (readyState, message) =>
        reduceTaxReturnDelivery(readyState, message.message),
      // Outbound folder commands
      formationFieldChanged: (readyState, message) =>
        callSdk(readyState, {
          type: 'folders/setFolderInputs',
          folderId: readyState.folderId,
          inputs: { [message.key]: message.value }
        }),
      requestEnqueued: (readyState, message) => callSdk(readyState, message.request),
      unlockClientOrganizer: (readyState, message) =>
        callSdk(readyState, message.request, {
          onSuccess: () => ({ type: 'unlockClientOrganizerSucceeded' })
        }),
      updateFolderTags: (readyState, message) =>
        callSdk(
          readyState,
          {
            type: 'folders/updateFolderTags',
            folderId: readyState.folderId,
            addTagIds: message.addTagIds,
            removeTagIds: message.removeTagIds
          },
          {
            onSuccess: () => ({
              type: 'showSnackbar',
              message: `Updated tags for ${formatFolderEntityName(
                readyState.folderState.folder.entities
              )}`
            })
          }
        ),
      openUploadDeleteDialog: (readyState, message) => ({
        ...readyState,
        clientUploadDialog: { type: 'confirmDelete', doc: message.doc }
      }),
      openUploadReasonDialog: (readyState, message) => ({
        ...readyState,
        clientUploadDialog: {
          type: 'setIncompleteReason',
          checklistItem: message.checklistItem,
          reason: message.checklistItem.incompleteReason
        }
      }),
      closeUploadDialog: (readyState) => ({ ...readyState, clientUploadDialog: undefined }),
      uploadReasonChanged: (readyState, message) =>
        match(readyState.clientUploadDialog)
          .with({ type: 'setIncompleteReason' }, (dialog) => ({
            ...readyState,
            clientUploadDialog: { ...dialog, reason: message.reason }
          }))
          .otherwise(() => {
            throw new Error('No upload reason dialog is open')
          }),
      saveUploadReason: saveUploadReason,
      removeUploadReason: saveUploadReason,
      confirmUploadDelete: (readyState) =>
        match(readyState.clientUploadDialog)
          .with({ type: 'confirmDelete' }, ({ doc }) =>
            callSdk(
              { ...readyState, clientUploadDialog: undefined },
              { type: 'folders/deleteDocument', folderId: readyState.folderId, documentId: doc.id }
            )
          )
          .otherwise(() => {
            throw new Error('No upload delete dialog is open')
          }),
      paymentLinkOpened: (readyState) => ({
        ...readyState,
        paymentLinkDialog: match(folderInvoice(readyState.folderState.payInvoice))
          .with(undefined, () => ({ url: '', required: true }))
          .otherwise(({ url, required }) => ({ url, required }))
      }),
      paymentLinkClosed: (readyState) => ({ ...readyState, paymentLinkDialog: undefined }),
      deliveryRequested: (readyState, message) =>
        match(message.emailsManagedBy)
          .returnType<STFolderUpdate>()
          .with('karbon', () => ({
            ...readyState,
            karbonDeliveryDialog: { contractId: message.contractId }
          }))
          .with('stanfordtax', () =>
            updateDeliveryWizard(
              {
                ...readyState,
                organizerWrites: reopenOrganizerDraft(
                  readyState.organizerWrites,
                  organizerDraft({
                    folderId: readyState.folderId,
                    target: { type: 'taxReturnDelivery', contractId: message.contractId }
                  })
                )
              },
              {
                type: 'opened',
                users: readyState.folderState.users,
                folderId: readyState.folderId,
                contractId: message.contractId
              }
            )
          )
          .exhaustive(),
      deliveryChecklistRequested: (readyState) =>
        applyNavigationRequest(
          { ...readyState, deliveryWizard: initialDeliveryWizard },
          navigateToChecklist('delivery')
        ),
      karbonDeliveryClosed: (readyState) => ({ ...readyState, karbonDeliveryDialog: undefined }),
      karbonLinkCopyRequested: (readyState, message) => [
        readyState,
        copyTextToClipboard(message.url, 'Copied link')
      ],
      paymentLinkEdited: (readyState, message) =>
        match(readyState.paymentLinkDialog)
          .with(P.nonNullable, (dialog) => ({
            ...readyState,
            paymentLinkDialog: { ...dialog, ...message.edit }
          }))
          .otherwise(() => {
            throw new Error('No payment link dialog is open')
          }),
      // Like the upload dialogs, Save and Remove close at once; the request queue
      // owns the write and subsequent live folder updates.
      paymentLinkSaved: savePaymentLink,
      paymentLinkRemoved: savePaymentLink,
      reportDocumentIssue: (readyState, message) =>
        callSdk(readyState, message.request, {
          onSuccess: () => ({
            type: 'showSnackbar',
            message: `Reported issue for document ${message.documentName}`
          })
        }),
      deleteAllDocuments: (readyState) => deleteAllDocuments(readyState),
      previewQuestionnaire: (readyState) => [readyState, openQuestionnairePreview(readyState)],
      markQuestionnaireAsCompleted: (readyState, message) =>
        callSdk(readyState, message.request, {
          // Use the shared questionnaire-progress slot with wait=0 so completion
          // supersedes any pending debounced progress autosaves.
          schedule: {
            queue: defaultQueue(message.request.folderId),
            wait: Duration.zero,
            uniqueKey: QUESTIONNAIRE_PROGRESS_SLOT_UNIQUE_KEY
          }
        }),
      markDocumentAsRead: (readyState, message) =>
        callSdk(readyState, {
          type: 'folders/markDocumentAsRead',
          folderId: message.folderId,
          documentId: message.documentId
        }),
      deleteDocument: (readyState, message) =>
        callSdk(
          readyState,
          {
            type: 'folders/deleteDocument',
            folderId: message.folderId,
            documentId: message.documentId
          },
          {
            onSuccess: () => ({
              type: 'showSnackbar',
              message: `Deleted document ${message.documentName}`
            })
          }
        ),
      downloadDocument: (readyState, message) => [
        readyState,
        Effect.gen(function* () {
          const sdk = yield* SdkClient
          const downloads = yield* BrowserDownloads
          const result = yield* sdk.send({
            ...message,
            type: 'folders/prepareDocumentDownload',
            folderId: readyState.folderId
          })
          yield* downloads.save(result.downloadUrl, result.filename)

          if (message.pages == null) {
            yield* sdk.send({
              type: 'folders/markFolderDocumentsExported',
              folderId: readyState.folderId,
              documentIds: [message.documentId],
              exportFormat: 'source'
            })
          }
          return null
          // No failure has a product response yet, so an unexpected one is a defect.
        }).pipe(Effect.orDie)
      ],
      copyWorkpaperLink: (readyState, message) => [
        readyState,
        copyTextToClipboard(message.url, 'Copied workpaper link to clipboard')
      ],
      copyText: (readyState, message) => [
        readyState,
        copyTextToClipboard(message.text, 'Copied value to clipboard')
      ],
      refreshTaxReturn: (readyState, message) => {
        const taxReturnId = readyState.folderState.folder.taxReturnId
        if (!taxReturnId) {
          return readyState
        }

        return callSdk(
          readyState,
          {
            type: 'taxReturns/pullTaxReturn',
            organizationId: readyState.folderState.organization.id,
            provider: 'ultratax',
            taxReturnId
          },
          {
            onSuccess: (result) =>
              result.status == 'pulled'
                ? {
                    type: 'showSnackbar',
                    message: 'Pulled latest UltraTax return'
                  }
                : {
                    type: 'showTaxReturnPullStatusDialog',
                    status: result.status
                  },
            failure: {
              transient: () => ({
                type: 'showTaxReturnPullErrorDialog',
                message: 'Something went wrong. Please try again.'
              }),
              httpStatus: (error) => {
                const message = resolveKnownTaxReturnLinkErrorMessage(error)
                return message ? { type: 'showTaxReturnPullErrorDialog', message } : undefined
              }
            }
          }
        )
      },
      pullInitialTaxReturnAndLink: (readyState, message) => [
        readyState,
        pullInitialTaxReturnAndLink({
          folderId: readyState.folderId,
          organizationId: readyState.folderState.organization.id,
          provider: message.provider,
          providerId: message.providerId,
          taxYear: readyState.folderState.folder.year,
          returnType: readyState.folderState.folder.type
        })
      ],
      renameDocument: (readyState, message) =>
        callSdk(
          readyState,
          {
            type: 'folders/renameDocument',
            folderId: message.folderId,
            documentId: message.documentId,
            documentName: message.newDocumentName
          },
          {
            onSuccess: () => ({
              type: 'showSnackbar',
              message: `Renamed document ${message.oldDocumentName} to ${message.newDocumentName}`
            }),
            failure: {
              httpStatus: (error) => {
                if (error.response.status != 422) return undefined
                return { type: 'showErrorSnackbar', message: 'Document name is invalid.' }
              }
            }
          }
        ),
      moveDocument: (readyState, message) =>
        callSdk(
          readyState,
          {
            type: 'folders/moveDocument',
            folderId: message.folderId,
            documentId: message.documentId,
            documentTypeId: message.documentTypeId
          },
          {
            onSuccess: () => ({
              type: 'showSnackbar',
              message: `${message.documentName} moved to ${message.destinationLabel}`
            })
          }
        ),
      linkDocumentTaxReturnUnit: (readyState, message) =>
        callSdk(
          readyState,
          {
            type: 'folders/linkDocumentTaxReturnUnit',
            folderId: message.folderId,
            documentId: message.documentId,
            taxReturnUnitPath: message.taxReturnUnitPath
          },
          {
            onSuccess: () => ({
              type: 'showSnackbar',
              message: `${message.documentName} linked to tax return unit`
            })
          }
        ),
      // Local effects / UI feedback
      showSnackbar: (readyState, message) => [readyState, showSnackbar(message.message)],
      showErrorSnackbar: (readyState, message) => [readyState, showErrorSnackbar(message.message)],
      showTaxReturnPullStatusDialog: (readyState, message) => ({
        ...readyState,
        taxReturnPullStatusDialog: { status: message.status }
      }),
      showTaxReturnPullErrorDialog: (readyState, message) => ({
        ...readyState,
        taxReturnPullStatusDialog: { message: message.message }
      }),
      closeTaxReturnPullStatusDialog: (readyState) => ({
        ...readyState,
        taxReturnPullStatusDialog: undefined
      }),
      unlockClientOrganizerSucceeded: (readyState) => [
        readyState,
        showSnackbar('Unlocked questionnaire')
      ]
    }
  }
)

function updateDeliveryWizard(
  state: STFolderModuleState,
  message: DeliveryWizardMessage
): STFolderUpdate {
  return deliveryWizardChild.update(
    state.deliveryWizard,
    message,
    (deliveryWizard) => ({ ...state, deliveryWizard }),
    {
      deliveryRequested: (request, state) =>
        startOrganizerWrite(
          state,
          { type: 'emailSendRequested', email: request.email },
          deliverReturn(request)
        ),
      draftSaveRequested: (request, state) => startOrganizerWrite(state, request)
    }
  )
}

function reduceTaxReturnDelivery(
  readyState: STFolderReadyState,
  message: TaxReturnDeliveryMessage
): STFolderReadyUpdate {
  const update = updateTaxReturnDelivery(readyState, message)
  let nextState = readyState
  if (update.state != readyState.taxReturnDelivery) {
    nextState = { ...readyState, taxReturnDelivery: update.state }
  }

  return match(update)
    .returnType<STFolderReadyUpdate>()
    .with({ type: 'stay' }, () => nextState)
    .with({ type: 'viewer' }, ({ messages }) =>
      Update.pipe(
        nextState,
        ...messages.map(
          (message) => (state: STFolderReadyState) =>
            routeViewerChild(state, 'pdf', { type: 'viewer', message })
        )
      )
    )
    .with({ type: 'leave' }, ({ navigation }) =>
      Update.pipe(discardDeliveryFieldDrafts(nextState), clearSelectedAnnotation, (state) => {
        if (!navigation) return state
        return applyNavigationRequest(state, navigation)
      })
    )
    .with({ type: 'save' }, (save) => {
      const failed = {
        type: 'showErrorSnackbar' as const,
        message: save.failureMessage
      }
      return callSdk(nextState, save.request, {
        onSuccess: save.onSuccess,
        failure: {
          transient: () => failed,
          httpStatus: () => failed
        }
      })
    })
    .exhaustive()
}

function advanceQueueRuntime(
  readyState: STFolderReadyState,
  message: QueueRuntimeMessage
): STFolderReadyUpdate {
  const queueItemId = queueRuntimeMessageId(message)
  if (!isCurrentQueueEvent(readyState, queueItemId)) {
    return readyState
  }
  return advanceReadyQueueRuntime(readyState, message)
}

function saveUploadReason(
  readyState: STFolderReadyState,
  message: Extract<STFolderMessage, { type: 'saveUploadReason' | 'removeUploadReason' }>
): STFolderReadyUpdate {
  return match(readyState.clientUploadDialog)
    .with({ type: 'setIncompleteReason' }, ({ checklistItem, reason }) => {
      const next = { ...readyState, clientUploadDialog: undefined }
      const incompleteReason = match(message.type)
        .with('removeUploadReason', () => undefined)
        .with('saveUploadReason', () => reason)
        .exhaustive()
      if (
        message.type == 'saveUploadReason' &&
        (reason == undefined || reason == checklistItem.incompleteReason)
      )
        return next
      if (message.type == 'removeUploadReason' && checklistItem.incompleteReason == undefined)
        return next
      return callSdk(next, {
        type: 'folders/setChecklistItemIncompleteReasons',
        folderId: readyState.folderId,
        options: [{ checklistItemId: checklistItem.id, incompleteReason }]
      })
    })
    .otherwise(() => {
      throw new Error('No upload reason dialog is open')
    })
}

function savePaymentLink(
  readyState: STFolderReadyState,
  message: Extract<STFolderMessage, { type: 'paymentLinkSaved' | 'paymentLinkRemoved' }>
): STFolderReadyUpdate {
  return match(readyState.paymentLinkDialog)
    .with(P.nonNullable, (draft) =>
      callSdk(
        { ...readyState, paymentLinkDialog: undefined },
        setInvoiceRequest(
          readyState.folderId,
          match(message.type)
            .with('paymentLinkSaved', () => draft)
            .with('paymentLinkRemoved', () => null)
            .exhaustive()
        )
      )
    )
    .otherwise(() => {
      throw new Error('No payment link dialog is open')
    })
}

function reduceFormationConfigState<T extends STFolderReadyState>(
  state: T,
  message: FormationConfigMessage
): T {
  if (state.formationConfig.tag != 'loading') {
    throw new Error(`Unexpected formationConfigLoaded message while ${state.formationConfig.tag}`)
  }

  return {
    ...state,
    formationConfig: {
      tag: 'loaded',
      schemaId: state.formationConfig.schemaId,
      config: message.config
    }
  }
}

function getSelectedDocForViewer(state: STFolderReadyState): STDocument | undefined {
  if (!state.selectedDocId) {
    return undefined
  }
  return state.folderState.documents.find((doc) => doc.id == state.selectedDocId)
}

function getSelectedDocKind(doc: STDocument | undefined): ViewerDocKind {
  if (!doc) {
    return 'none'
  }
  return resolveViewerDocKind(doc.mimeType)
}

function hasFormationConfigForSchema(
  formationConfig: FolderFormationConfigState,
  schemaId: string
): boolean {
  if (formationConfig.tag == 'idle') {
    return false
  }
  return formationConfig.schemaId == schemaId
}

function resolveLoadedFormationConfig(state: STFolderReadyState): FormConfig | undefined {
  return resolveLoadedFormationConfigForSchema(state.formationConfig, state.folderState.schemaId)
}

function resolveLoadedFormationConfigForSchema(
  formationConfig: FolderFormationConfigState,
  schemaId: string
): FormConfig | undefined {
  if (formationConfig.tag != 'loaded') {
    return undefined
  }
  if (formationConfig.schemaId != schemaId) {
    return undefined
  }
  return formationConfig.config
}

/**
 * Where a request scrolls now. Formation placement depends on the loaded
 * layout, so a Formation target awaits its config instead of being treated as
 * missing. Opening a document, or a target the folder no longer contains, has
 * nowhere to scroll.
 */
function resolveNavigationLocation(
  state: STFolderReadyState,
  request: NavigationRequest
): ViewerNavigation['location'] {
  if (request.target.type == 'document') return null
  const doc = state.folderState.documents.find((candidate) => candidate.id == request.documentId)
  if (!doc) return null
  if (getSelectedDocKind(doc) == 'formation' && !resolveLoadedFormationConfig(state)) {
    return 'awaiting'
  }
  const location = resolveDocumentLocationForTarget({
    folderState: { ...state.folderState, checklistItems: selChecklistItems(state) },
    formationLayout: selFormationLayout(state),
    doc,
    target: request.target
  })
  return location ?? null
}

// Following an annotation selects it; opening a document or jumping to a page
// clears the selection.
function navigationSelection(target: NavigationRequest['target']): ViewerSelection {
  return match(target)
    .returnType<ViewerSelection>()
    .with({ type: 'annotation' }, ({ annotationId }) => ({ annotationId }))
    .with({ type: P.union('document', 'page', 'checklist') }, () => 'clear')
    .exhaustive()
}

function resolveViewerNavigation(
  state: STFolderReadyState,
  documentId: string
): ViewerNavigation | null {
  const navigation = state.navigation
  if (navigation?.request.documentId != documentId) return null
  return {
    token: navigation.token,
    location: resolveNavigationLocation(state, navigation.request),
    selection: navigationSelection(navigation.request.target),
    behavior: navigation.behavior
  }
}

function resolveViewerProjection(state: STFolderReadyState): ViewerProjection {
  const selectedDoc = getSelectedDocForViewer(state)
  const docKind = getSelectedDocKind(selectedDoc)
  if (!selectedDoc || docKind == 'none') {
    return { scope: state.viewerScope, presentation: CLOSED_VIEWER_PRESENTATION }
  }
  const scope = resolveViewerScopeFromDocKind(docKind)
  const navigation = resolveViewerNavigation(state, selectedDoc.id)
  const sessionKey = `${state.folderId}:${selectedDoc.id}`

  const delivery = taxReturnDeliveryDocumentState(
    state.folderState.taxReturnDelivery,
    state.folderState.taxReturnDeliveryDrafts,
    selectedDoc.id
  )?.contract
  const editable =
    delivery == undefined &&
    (docKind == 'pdf' || (docKind == 'formation' && selectedDoc.documentTypeId == 'stanfordtax'))
  const formationLayout = selFormationLayout(state)
  const annotations: Record<string, STViewerAnnotation> = {}
  if (delivery?.status != 'published') {
    const schema = state.folderState.dataSchemas[selectedDoc.documentTypeId!] as
      | JSONSchema
      | undefined
    Object.assign(
      annotations,
      selectedDoc.annotations,
      match(state.pushTaxReturn && selectedDoc.documentTypeId != 'tax_return_packet')
        .with(true, () => resolveDocumentDataFieldAnnotations(selectedDoc, schema))
        .with(false, () => ({}))
        .exhaustive()
    )
    Object.assign(
      annotations,
      resolveViewerCommentAnnotations({
        comments: state.folderState.comments,
        documentId: selectedDoc.id,
        editable,
        pageNumberByAnchor: formationLayout.pageNumberByAnchor
      })
    )
    if (delivery?.status == 'draft') {
      Object.assign(
        annotations,
        contractDraftFieldAnnotations(
          delivery,
          match(selTaxReturnDeliveryPolicy(state).canEditFields)
            .with(true, () => 'editing' as const)
            .with(false, () => 'viewing' as const)
            .exhaustive()
        )
      )
    }
  }

  if (docKind == 'formation') {
    // Until its config loads, the Formation viewer stays empty and the
    // navigation waits there, cancelling whatever the previous intent was.
    if (!resolveLoadedFormationConfig(state)) {
      return { scope, presentation: { ...CLOSED_VIEWER_PRESENTATION, navigation } }
    }

    return {
      scope,
      presentation: {
        document: {
          sessionKey,
          documentId: selectedDoc.id,
          source: {
            pages: formationLayout.pages.map((_, index) => ({
              pageNumber: index + 1,
              width: 650,
              height: 1000
            }))
          },
          pageRotations: {},
          deletedPageNumbers: []
        },
        annotations,
        navigation,
        initialZoom: 1.125
      }
    }
  }

  if (docKind == 'formation-checklist') {
    return {
      scope,
      presentation: {
        document: {
          sessionKey,
          documentId: selectedDoc.id,
          source: {
            pages: resolveChecklistViewerPages(selChecklistItems(state))
          },
          pageRotations: {},
          deletedPageNumbers: []
        },
        annotations: {},
        navigation,
        initialZoom: 0.75
      }
    }
  }

  return {
    scope,
    presentation: {
      document: match(selectedDoc.urls.view)
        .with(P.nonNullable, (url) => ({
          sessionKey,
          documentId: selectedDoc.id,
          source: { src: url },
          pageRotations: selectedDoc.pageRotations,
          deletedPageNumbers: selectedDoc.deletedPageNumbers ?? []
        }))
        .with(P.nullish, () => null)
        .exhaustive(),
      annotations,
      navigation,
      initialZoom: 1
    }
  }
}

function ensureReadyFormationConfig(state: STFolderReadyState): STFolderReadyUpdate {
  const schemaId = state.folderState.schemaId
  if (hasFormationConfigForSchema(state.formationConfig, schemaId)) {
    return state
  }

  return {
    ...state,
    formationConfig: { tag: 'loading', schemaId }
  }
}

function shouldEnsureFormationConfigForTransition(
  prevState: STFolderModuleState,
  nextState: STFolderReadyState
): boolean {
  if (prevState.status != 'ready') {
    return true
  }

  return prevState.folderState.schemaId != nextState.folderState.schemaId
}

function reconcileViewerPhase(
  prevState: STFolderModuleState,
  nextState: STFolderReadyState
): STFolderReadyUpdate {
  return Update.pipe(
    nextState,
    (stateWithFormationConfig) =>
      shouldEnsureFormationConfigForTransition(prevState, stateWithFormationConfig)
        ? ensureReadyFormationConfig(stateWithFormationConfig)
        : stateWithFormationConfig,
    (stateAfterDerivedResources) => {
      const { scope, presentation } = resolveViewerProjection(stateAfterDerivedResources)
      return routeReadyViewerStateByScope(stateAfterDerivedResources, scope, {
        type: 'present',
        presentation
      })
    }
  )
}

function handleViewerMessage(
  state: STFolderModuleState,
  message: STDocViewerRuntimeMessage
): STFolderUpdate {
  return Update.pipe(routeViewerStateByScope(state, state.viewerScope, message), (next) =>
    revealCommentEditor(state, next)
  )
}

/**
 * The comment composer exists only in the comments sidebar. When a comment
 * draft or edit begins (for example, placing a new comment with the comment
 * tool), expand a collapsed sidebar so the editor is visible. Only the start
 * of editing expands it; the user can still hide the sidebar afterwards.
 */
function revealCommentEditor(
  prev: STFolderModuleState,
  next: STFolderModuleState
): STFolderModuleState {
  if (next.status != 'ready') return next
  const editingCommentId = selEditingCommentId(next)
  if (editingCommentId == undefined || editingCommentId == selEditingCommentId(prev)) return next
  return { ...next, sidebarVisibility: 'expanded' }
}

function selEditingCommentId(state: STFolderModuleState): string | undefined {
  // The annotations plugin is installed only once the viewer is configured for
  // a document that uses it; before that (e.g. initial binder load) or in other
  // viewer scopes, nothing can be editing a comment.
  if (state.viewer.pluginStates[ANNOTATIONS_PLUGIN_ID] == undefined) return undefined
  const selection = selAnnotationsPluginState(state.viewer).selectionState
  if (selection.type != 'editing') return undefined
  const annotation = selActiveAnnotation(state.viewer)
  if (!annotation || !isCommentAnnotation(annotation)) return undefined
  return selection.annotationId
}

function handleViewerCommand(
  state: STFolderModuleState,
  command: DocumentViewerCommand
): STFolderUpdate {
  if (state.status != 'ready') {
    return state
  }

  if (command.type != 'pluginCommand') {
    return state
  }

  if (command.pluginId != 'annotations') {
    return state
  }

  const annotationsCommand = command.command as AnnotationsPluginCommand & {
    documentId: string
    annotation: STViewerAnnotation
  }

  const annotation = annotationsCommand.annotation
  if (annotation.type == 'field') return state

  const targetDocumentId = annotationsCommand.documentId

  switch (annotationsCommand.type) {
    case 'create':
    case 'update': {
      let documentAnnotation: DocumentAnnotation
      if (annotation.type == 'comment') {
        documentAnnotation = toBoundedCommentAnnotation(annotation)
      } else {
        documentAnnotation = annotation
      }

      return callSdk(state, {
        type:
          annotationsCommand.type == 'create'
            ? 'folders/addAnnotationToDocument'
            : 'folders/updateDocumentAnnotation',
        annotation: documentAnnotation,
        folderId: state.folderId,
        documentId: targetDocumentId
      })
    }
    case 'delete': {
      return callSdk(state, {
        type: 'folders/deleteDocumentAnnotation',
        folderId: state.folderId,
        documentId: targetDocumentId,
        annotationId: annotation.id
      })
    }
  }
}

function queueRuntimeMessageId(message: QueueRuntimeMessage): string {
  return message.type == 'settleGraceElapsed' ? message.itemId : message.requestId
}

function advanceReadyQueueRuntime(
  readyState: STFolderReadyState,
  message: QueueRuntimeMessage
): STFolderReadyUpdate {
  switch (message.type) {
    case 'requestStarted':
      return {
        ...readyState,
        requestQueue: markRequestStartedLedger(readyState.requestQueue, message.requestId)
      }
    case 'requestSucceeded':
      return Update.pipe(
        readyState,
        (state) => settleRequestResponse(state, message.requestId),
        emitQueuedRequestEvent(message.queuedOnSuccessMessage ?? null)
      )
    case 'requestUnauthorized':
      return Update.pipe(
        {
          ...readyState,
          requestQueue: markRequestFailedLedger(readyState.requestQueue, message.requestId)
        },
        handleRequestFailure(message.handling)
      )
    case 'requestFailed': {
      const failedState = {
        ...readyState,
        requestQueue: markRequestFailedLedger(readyState.requestQueue, message.requestId)
      }
      return Update.pipe(failedState, handleRequestFailure(message.handling))
    }
    case 'settleGraceElapsed':
      return readyState
  }
}

function emitQueuedRequestEvent(
  event: STFolderMessage | STFolderMessage[] | null
): (state: STFolderReadyState) => STFolderReadyUpdate {
  return (state) => (event == null ? state : [state, Effect.succeed(event)])
}

function clearSelectedAnnotation<T extends STFolderModuleState>(
  state: T
): UpdateResult<T, STFolderMessage> {
  if (state.viewerScope == 'formation-checklist') return state

  return routeViewerStateByScope(state, state.viewerScope, {
    type: 'viewer',
    message: toAnnotationsPluginMessage({ type: 'deselectAnnotation' })
  }) as UpdateResult<T, STFolderMessage>
}

function selectAnnotationTool(
  state: STFolderReadyState,
  toolName: 'select' | 'field'
): STFolderReadyUpdate {
  return routeReadyViewerStateByScope(state, state.viewerScope, {
    type: 'viewer',
    message: toAnnotationsPluginMessage({ type: 'selectTool', toolName })
  })
}

function handleRequestFailure(
  handling: RequestFailureHandling<STFolderMessage | STFolderMessage[]>
): (state: STFolderReadyState) => STFolderReadyUpdate {
  return (state) =>
    match(handling)
      .returnType<STFolderReadyUpdate>()
      .with({ type: 'unhandled' }, () =>
        Update.pipe(
          state,
          Update.effect(() => showErrorSnackbar('Something went wrong. Please try again.'))
        )
      )
      .with({ type: 'handled' }, ({ message }) => emitQueuedRequestEvent(message ?? null)(state))
      .exhaustive()
}

function applyNavigationRequest(
  state: STFolderReadyState,
  request: NavigationRequest
): STFolderReadyUpdate {
  if (!state.folderState.documents.some((doc) => doc.id == request.documentId)) {
    return state
  }
  if (request.documentId != state.selectedDocId) {
    if (selTaxReturnDeliveryDirty(state)) {
      return {
        ...state,
        taxReturnDelivery: requestDeliveryDeparture(state.taxReturnDelivery, {
          type: 'document',
          request: request
        })
      }
    }
  }
  const token = state.navigationSeq + 1
  const markAsRead = markDocumentAsReadEffectIfUnread(state, request.documentId)
  // State scoped to the selected document survives navigation within it
  // (e.g. jumping to a page) and resets when another document is selected.
  const documentScopedState = match(request.documentId == state.selectedDocId)
    .with(true, () => ({
      bundleSelection: state.bundleSelection,
      sidebarVisibility: state.sidebarVisibility,
      taxReturnDelivery: state.taxReturnDelivery
    }))
    .with(false, () => ({
      bundleSelection: null,
      sidebarVisibility: initialSidebarVisibility(state.folderState, request.documentId),
      taxReturnDelivery: initTaxReturnDeliveryState()
    }))
    .exhaustive()

  // A scroll target the folder no longer contains is ignored outright; opening
  // a document has nothing to place.
  if (request.target.type != 'document' && resolveNavigationLocation(state, request) == null) {
    return state
  }

  const nextState: STFolderReadyState = {
    ...state,
    navigationSeq: token,
    selectedDocId: request.documentId,
    expandedPaths: expandTreePath(state.expandedPaths, [request.documentId]),
    ...documentScopedState,
    navigation: {
      token,
      request,
      behavior: match(request.documentId == state.selectedDocId)
        .returnType<ScrollBehavior>()
        .with(true, () => 'smooth')
        .with(false, () => 'auto')
        .exhaustive()
    }
  }
  if (!markAsRead) {
    return nextState
  }
  return Update.pipe(
    nextState,
    Update.effect(() => markAsRead)
  )
}

function markDocumentAsReadEffectIfUnread(state: STFolderReadyState, docId: string) {
  const doc = state.folderState.documents.find((d) => d.id == docId)
  if (doc?.readStatus != 'unread') {
    return undefined
  }
  return Effect.succeed({
    type: 'markDocumentAsRead',
    folderId: state.folderId,
    documentId: docId
  } as const)
}

function runQuestionnaireCommand(
  state: STFolderReadyState,
  command: FolderQuestionnaireCommand
): STFolderReadyUpdate {
  switch (command.type) {
    case 'setFolderInput':
      return callSdk(state, {
        type: 'folders/setFolderInputs',
        folderId: state.folderId,
        inputs: { [command.inputKey]: command.value }
      })
    case 'deleteDocument':
      return callSdk(state, {
        type: 'folders/deleteDocument',
        folderId: command.folderId,
        documentId: command.docId
      })
    case 'markQuestionnaireAsCompleted':
      return callSdk(
        state,
        {
          type: 'folders/markQuestionnaireAsCompleted',
          folderId: command.folderId
        },
        {
          // See note above: completion cancels stale queued progress writes by
          // occupying the same scheduler slot immediately.
          schedule: {
            queue: defaultQueue(command.folderId),
            wait: Duration.zero,
            uniqueKey: QUESTIONNAIRE_PROGRESS_SLOT_UNIQUE_KEY
          }
        }
      )
    case 'saveIncompleteReasonsBeforeSubmit':
      return callSdk(
        state,
        {
          type: 'folders/setChecklistItemIncompleteReasons',
          folderId: command.folderId,
          options: command.incompleteReasonOptions
        },
        {
          onSuccess: () =>
            ({
              type: 'questionnaire',
              message: {
                type: 'questionnaireCommand',
                command: {
                  type: 'submitClientOrganizer',
                  folderId: command.folderId,
                  forceSubmit: command.forceSubmit
                }
              }
            }) as const,
          // Preserve prior behavior: if saving reasons fails, do not submit.
          failure: questionnaireSubmitFailureRecovery
        }
      )
    case 'submitClientOrganizer':
      return callSdk(
        state,
        {
          type: 'folders/submitClientOrganizer',
          folderId: command.folderId,
          forceSubmit: command.forceSubmit
        },
        {
          onSuccess: (response) =>
            ({
              type: 'questionnaire',
              message: {
                type: 'submitCompleted',
                response
              }
            }) as const,
          failure: questionnaireSubmitFailureRecovery
        }
      )
    case 'saveQuestionnaireProgressNow':
      return callSdk(
        state,
        {
          type: 'folders/saveQuestionnaireProgress',
          folderId: command.folderId,
          pageId: command.pageId,
          progress: command.progress
        },
        {
          schedule: {
            queue: defaultQueue(command.folderId),
            wait: Duration.zero,
            uniqueKey: QUESTIONNAIRE_PROGRESS_SLOT_UNIQUE_KEY
          },
          failure: { transient: 'ignore' }
        }
      )
    case 'saveQuestionnaireProgressAutosave':
      return callSdk(
        state,
        {
          type: 'folders/saveQuestionnaireProgress',
          folderId: command.folderId,
          pageId: command.pageId,
          progress: command.progress
        },
        {
          schedule: {
            queue: defaultQueue(command.folderId),
            wait: QUESTIONNAIRE_PROGRESS_WAIT,
            uniqueKey: QUESTIONNAIRE_PROGRESS_SLOT_UNIQUE_KEY
          },
          failure: { transient: 'ignore' }
        }
      )
    case 'writeMfaGrantCookie':
      return Update.pipe(
        state,
        Update.effect(() =>
          Effect.gen(function* () {
            const cookies = yield* BrowserCookies
            const cookieHeader = yield* cookies.read
            yield* cookies.write(buildGrantCookieAssignment(cookieHeader, command.folderId))
            return null
          })
        )
      )
  }
}

const questionnaireSubmitFailureRecovery = {
  transient: questionnaireSubmitFailed,
  httpStatus: questionnaireSubmitFailed
} satisfies FolderRequestFailureRecovery

function questionnaireSubmitFailed(): STFolderMessage[] {
  return [
    {
      type: 'questionnaire',
      message: { type: 'submitRequestFailed' }
    },
    { type: 'showErrorSnackbar', message: 'Something went wrong. Please try again.' }
  ]
}

function reportingPhase(
  prevState: STFolderModuleState,
  nextState: STFolderReadyState
): STFolderReadyUpdate {
  if (prevState.status == 'ready' && prevState.selectedDocId == nextState.selectedDocId) {
    return nextState
  }
  return Update.pipe(
    nextState,
    Update.effect(() =>
      Effect.gen(function* () {
        const errorReporting = yield* ErrorReporting
        yield* errorReporting.setTags({
          document_id: nextState.selectedDocId
        })
        return null
      })
    )
  )
}

/** Delivery starts collapsed so the packet is read first; later toggles keep the user's choice. */
function initialSidebarVisibility(
  folder: AuthorizedOpenFolderState,
  documentId: string | undefined
): SidebarVisibility {
  if (documentId == undefined) return 'expanded'
  return match(
    taxReturnDeliveryDocumentState(
      folder.taxReturnDelivery,
      folder.taxReturnDeliveryDrafts,
      documentId
    )
  )
    .with(undefined, () => 'expanded' as const)
    .otherwise(() => 'collapsed' as const)
}

function advanceToReady(
  state: STFolderLoadingState,
  folderState: AuthorizedOpenFolderState,
  folderStateVsn: number
): STFolderReadyUpdate {
  const selectedDocId = getDocumentSections(folderState)[0]?.items[0]?.id
  const readyState: STFolderReadyState = {
    ...folderModuleLifetimeState(state),
    status: 'ready',
    folderId: state.folderId,
    folderViewMode: state.folderViewMode,
    navigationSeq: state.navigationSeq,
    checklist: {
      folderId: folderState.folderId,
      year: folderState.folder.year,
      returnType: folderState.folder.type
    },
    folderState,
    folderStateVsn,
    selectedDocId,
    navigation: undefined,
    formationConfig: { tag: 'idle' },
    questionnaire: initFolderQuestionnaireState(),
    bulkUpload: initBulkUploadState(),
    taxReturnPullStatusDialog: undefined,
    clientUploadDialog: undefined,
    paymentLinkDialog: undefined,
    karbonDeliveryDialog: undefined,
    bundleSelection: null,
    sidebarVisibility: initialSidebarVisibility(folderState, selectedDocId),
    expandedPaths: [],
    taxReturnDelivery: initTaxReturnDeliveryState(),
    requestQueue: emptyRequestQueue<FolderRequest>()
  }
  return Update.pipe(
    mapQuestionnaireUpdate(
      readyState,
      reconcileFolderQuestionnaire({
        state: readyState.questionnaire,
        stFolder: readyState
      })
    ),
    (nextState) => {
      if (!state.deferredNavigation) return nextState
      // Replay intent after initialization so selection, expansion, scrolling and
      // read tracking follow the same transition as navigation in a loaded binder.
      return applyNavigationRequest(nextState, state.deferredNavigation)
    },
    reportEnteredReadyState
  )
}

function advanceToUnauthorized(
  state: STFolderLoadingState | STFolderReadyState | STFolderUnauthorizedState,
  folderState: UnauthorizedOpenFolderState,
  folderStateVsn: number
): STFolderUpdate {
  const unauthorizedState: STFolderUnauthorizedState = {
    ...folderModuleLifetimeState(state),
    status: 'unauthorized',
    folderId: state.folderId,
    folderViewMode: state.folderViewMode,
    folderState,
    folderStateVsn
  }
  return routeViewerStateByScope(unauthorizedState, unauthorizedState.viewerScope, {
    type: 'present',
    presentation: CLOSED_VIEWER_PRESENTATION
  })
}

function restartFolderLoading(state: STFolderUnauthorizedState): STFolderLoadingState {
  return {
    ...folderModuleLifetimeState(state),
    status: 'loading',
    navigationSeq: 0,
    folderId: state.folderId,
    folderViewMode: state.folderViewMode,
    deferredNavigation: undefined
  }
}

function folderModuleLifetimeState(state: STFolderModuleState) {
  return {
    deliveryWizard: state.deliveryWizard,
    organizerWrites: state.organizerWrites,
    nextRequestId: state.nextRequestId,
    currentUserId: state.currentUserId,
    pushTaxReturn: state.pushTaxReturn,
    viewerScope: state.viewerScope,
    viewer: state.viewer
  }
}

function reduceRouteChanged(state: STFolderModuleState, route: AppRoute | null): STFolderUpdate {
  const routeContext = routeToFolderContext(route)
  if (routeContext.type == 'no_folder') {
    if (state.status == 'idle') {
      return state
    }
    const clearedState: Extract<STFolderModuleState, { status: 'idle' }> = {
      ...folderModuleLifetimeState(state),
      // The folder, not the wizard, owns a delivery still being written.
      deliveryWizard: initialDeliveryWizard,
      status: 'idle'
    }
    return routeViewerStateByScope(clearedState, clearedState.viewerScope, {
      type: 'present',
      presentation: CLOSED_VIEWER_PRESENTATION
    })
  }

  const context = routeContext.context
  if (state.status == 'idle' || state.folderId != context.folderId) {
    const loadingState: STFolderLoadingState = {
      ...folderModuleLifetimeState(state),
      deliveryWizard: initialDeliveryWizard,
      status: 'loading',
      navigationSeq: 0,
      folderId: context.folderId,
      folderViewMode: context.viewMode,
      deferredNavigation: undefined
    }
    return Update.pipe<STFolderModuleState, STFolderMessage>(loadingState, (nextState) =>
      routeViewerStateByScope(nextState, nextState.viewerScope, {
        type: 'present',
        presentation: CLOSED_VIEWER_PRESENTATION
      })
    )
  }

  if (state.folderViewMode == context.viewMode) {
    return state
  }

  return { ...state, folderViewMode: context.viewMode }
}

function reportEnteredReadyState(state: STFolderReadyState): STFolderReadyUpdate {
  if (
    state.folderViewMode == 'organization_folder' ||
    state.folderViewMode == 'organization_folder_debug' ||
    state.folderViewMode == 'organization_preview_questionnaire'
  ) {
    return callSdk(
      state,
      {
        type: 'folders/trackFolderViewed',
        folderId: state.folderId
      },
      {
        failure: { transient: 'ignore' }
      }
    )
  }

  if (state.folderViewMode != 'client_folder') {
    return state
  }

  return Update.pipe(
    callSdk(
      state,
      {
        type: 'folders/markAsOpened',
        folderId: state.folderId
      },
      {
        failure: { transient: 'ignore' }
      }
    ),
    Update.effect((nextState: STFolderReadyState) =>
      Effect.gen(function* () {
        const analytics = yield* Analytics
        yield* analytics.setGroup('organization', nextState.folderState.organization.id)
        yield* analytics.setGroup('folder', nextState.folderId)
        yield* analytics.track('client_folder_opened')
        return null
      })
    )
  )
}

function folderTopicTasks(state: STFolderModuleState): STFolderSubscription[] {
  if (!state.currentUserId || state.status == 'idle') {
    return []
  }

  return [observeFolderTopic(state.currentUserId, state.folderId)]
}

function requestQueueSubscriptions(state: STFolderModuleState): STFolderSubscription[] {
  switch (state.status) {
    case 'ready': {
      return [observeFolderRequestScheduler()]
    }
    default:
      return []
  }
}

function formationConfigTasks(state: STFolderModuleState): STFolderSubscription[] {
  if (state.status != 'ready' || state.formationConfig.tag != 'loading') {
    return []
  }
  return [loadFormationConfig(state.formationConfig.schemaId)]
}

function questionnaireTasks(state: STFolderModuleState): STFolderSubscription[] {
  if (state.status != 'ready' || state.questionnaire.status != 'ready') {
    return []
  }

  const tasks: STFolderSubscription[] = []
  if (state.questionnaire.mfaGate.status == 'checking') {
    tasks.push(readQuestionnaireMfaGrant(state.folderId))
  }
  if (state.questionnaire.dashboard.status == 'validating') {
    tasks.push(validateQuestionnaireSubmit(state.folderId))
  }

  const autosave = selQuestionnaireAutosave(state.questionnaire, { stFolder: state })
  if (autosave) {
    tasks.push(waitForQuestionnaireAutosave(autosave.pageId, autosave.progress))
  }
  return tasks
}

function settleRequestResponse(state: STFolderReadyState, requestId: string): STFolderReadyUpdate {
  const nextRequestQueue = markRequestSettledAtVsnLedger(
    state.requestQueue,
    requestId,
    state.folderStateVsn
  )
  if (nextRequestQueue == state.requestQueue) {
    return state
  }

  return {
    ...state,
    requestQueue: nextRequestQueue
  }
}

function bulkUploadTasks(state: STFolderModuleState): STFolderSubscription[] {
  if (state.status != 'ready') return []
  const session = state.bulkUpload.session
  return session ? [resolveBulkUpload(session.upload.id)] : []
}

function showSnackbar(message: string) {
  return Effect.gen(function* () {
    const snackbar = yield* Snackbar
    yield* snackbar.show(message)
    return null
  })
}

function deleteAllDocuments(state: STFolderReadyState): STFolderReadyUpdate {
  return callSdkMany(
    state,
    selUploadedDocuments(state).map((document) => ({
      type: 'folders/deleteDocument',
      folderId: state.folderId,
      documentId: document.id
    }))
  )
}

function openQuestionnairePreview(state: STFolderReadyState) {
  return Effect.gen(function* () {
    const routing = yield* Routing
    yield* routing.openRoute({
      name: 'organization_preview_questionnaire',
      organizationSlug: state.folderState.organization.slug,
      folderId: state.folderId
    })
    return null
  })
}

function showErrorSnackbar(message: string) {
  return Effect.gen(function* () {
    const snackbar = yield* Snackbar
    yield* snackbar.show(message, { variant: 'error' })
    return null
  })
}

function pullInitialTaxReturnAndLink({
  folderId,
  organizationId,
  provider,
  providerId,
  taxYear,
  returnType
}: {
  folderId: string
  organizationId: string
  provider: Provider
  providerId: string
  taxYear: number
  returnType: string
}) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient

    const pulledTaxReturn = yield* recoverTaxReturnLinkRequest(
      sdk.send({
        type: 'taxReturns/pullInitialTaxReturn',
        organizationId,
        provider,
        providerId,
        taxYear,
        returnType
      })
    )

    if (!pulledTaxReturn.ok) {
      return {
        type: 'showTaxReturnPullErrorDialog',
        message: pulledTaxReturn.message
      } as const
    }

    if (pulledTaxReturn.response.status != 'pulled') {
      return {
        type: 'showTaxReturnPullStatusDialog',
        status: pulledTaxReturn.response.status
      } as const
    }

    const linkedTaxReturn = yield* recoverTaxReturnLinkRequest(
      sdk.send({
        type: 'folders/linkTaxReturn',
        folderId,
        taxReturnId: pulledTaxReturn.response.taxReturnId
      })
    )

    if (!linkedTaxReturn.ok) {
      return {
        type: 'showTaxReturnPullErrorDialog',
        message: linkedTaxReturn.message
      } as const
    }

    return null
  })
}

function recoverTaxReturnLinkRequest<A, R>(effect: Effect.Effect<A, SdkFailure, R>) {
  return runSdkRequest(effect).pipe(
    Effect.flatMap((result) =>
      match(result)
        .returnType<
          Effect.Effect<{ ok: true; response: A } | { ok: false; message: string }, never, never>
        >()
        .with({ ok: true }, (result) => Effect.succeed(result))
        .with({ ok: false, error: { _tag: 'HttpStatusError' } }, ({ error }) => {
          // An unrecognised status gets the same generic message as a network failure.
          const message =
            resolveKnownTaxReturnLinkErrorMessage(error) ??
            'Something went wrong. Please try again.'
          return Effect.succeed({ ok: false as const, message })
        })
        .with({ ok: false }, () =>
          Effect.succeed({
            ok: false as const,
            message: 'Something went wrong. Please try again.'
          })
        )
        .exhaustive()
    )
  )
}

function resolveKnownTaxReturnLinkErrorMessage(
  error: Extract<SdkFailure, { _tag: 'HttpStatusError' }>
): string | undefined {
  const body = error.response.body
  const code =
    typeof body == 'object' && body != null && 'error' in body && typeof body.error == 'string'
      ? body.error
      : undefined

  switch (code) {
    case 'desktop_agent_not_connected':
      return 'Desktop agent is not connected.'
    case 'desktop_agent_busy':
      return 'Desktop agent is busy. Please try again.'
    case 'tax_return_file_not_found':
      return 'UltraTax return not found. Check the client ID and try again.'
    case 'timed_out':
      return 'Timed out waiting for the desktop agent.'
    case 'unsupported_provider':
      return 'Only UltraTax is supported right now.'
    case 'invalid_tax_return_for_folder':
      return 'That tax return does not match this folder.'
    case 'tax_return_not_found':
      return 'Tax return not found.'
    default:
      return undefined
  }
}

function schedulerEventToMessage(event: STFolderSchedulerEvent): STFolderMessage {
  switch (event.type) {
    case 'requestStarted':
      return { type: 'requestStarted', requestId: event.requestId }
    case 'requestSucceeded':
      return {
        type: 'requestSucceeded',
        requestId: event.requestId,
        queuedOnSuccessMessage: toQueuedSchedulerMessage(event.message)
      }
    case 'requestFailed': {
      const handling = match(event.handling)
        .returnType<RequestFailureHandling<STFolderMessage | STFolderMessage[]>>()
        .with({ type: 'unhandled' }, () => ({ type: 'unhandled' }))
        .with({ type: 'handled' }, ({ message }) => {
          const queuedMessage = toQueuedSchedulerMessage(message)
          if (queuedMessage == null) {
            return { type: 'handled' }
          }
          return { type: 'handled', message: queuedMessage }
        })
        .exhaustive()

      if (isHttpStatusError(event.error) && event.error.response.status == 401) {
        return {
          type: 'requestUnauthorized',
          requestId: event.requestId,
          handling
        }
      }
      return {
        type: 'requestFailed',
        requestId: event.requestId,
        handling
      }
    }
  }
}

function toQueuedSchedulerMessage(message: unknown): STFolderMessage | STFolderMessage[] | null {
  if (message == null) {
    return null
  }
  if (Array.isArray(message)) {
    return message.every(isFolderMessage) ? message : null
  }
  return isFolderMessage(message) ? message : null
}

function isFolderMessage(message: unknown): message is STFolderMessage {
  return typeof message == 'object' && message != null && 'type' in message
}

function routeToFolderContext(route: AppRoute | null): RouteToFolderContext {
  return match(route)
    .with({ name: 'organization_folder', folderId: P.string }, ({ folderId }) => ({
      type: 'folder' as const,
      context: { folderId, viewMode: 'organization_folder' as const }
    }))
    .with({ name: 'organization_folder_debug', folderId: P.string }, ({ folderId }) => ({
      type: 'folder' as const,
      context: { folderId, viewMode: 'organization_folder_debug' as const }
    }))
    .with({ name: 'organization_preview_questionnaire', folderId: P.string }, ({ folderId }) => ({
      type: 'folder' as const,
      context: { folderId, viewMode: 'organization_preview_questionnaire' as const }
    }))
    .with({ name: 'client_folder', folderId: P.string }, ({ folderId }) => ({
      type: 'folder' as const,
      context: { folderId, viewMode: 'client_folder' as const }
    }))
    .otherwise(() => ({ type: 'no_folder' as const }))
}

function callSdk<RequestType extends FolderRequest['type']>(
  state: STFolderReadyState,
  request: Extract<FolderRequest, { type: RequestType }>,
  params?: {
    schedule?: RequestSchedule
    onSuccess?: (
      response: SDKResponseFor<RequestType>
    ) => STFolderMessage | STFolderMessage[] | null
    failure?: FolderRequestFailureRecovery
  }
): STFolderReadyUpdate {
  const queuedState = {
    ...state,
    nextRequestId: state.nextRequestId + 1,
    requestQueue: enqueueRequestLedger(state.requestQueue, state.nextRequestId.toString(), request)
  }
  const itemId = state.nextRequestId.toString()
  const queuedRequest = selectQueuedRequestById(queuedState.requestQueue, {
    requestId: itemId,
    type: request.type
  })
  if (!queuedRequest) {
    return queuedState
  }

  const schedule = params?.schedule ?? defaultSchedule(queuedRequest)
  const failure = params?.failure

  return [
    queuedState,
    Effect.gen(function* () {
      const scheduler = yield* RequestScheduler
      const sdk = yield* SdkClient

      yield* scheduler.schedule({
        job: {
          requestId: itemId,
          run: sdk.send(queuedRequest),
          onSucceededMessage: params?.onSuccess,
          recoverFailure:
            failure == null ? undefined : (error) => recoverFolderRequestFailure(failure, error)
        },
        schedule
      })
      return null
    })
  ] as const
}

type FolderRequestFailureMessage = STFolderMessage | STFolderMessage[] | null

type FolderRequestFailureRecovery = {
  transient?: 'ignore' | ((error: TransportError | TimeoutError) => FolderRequestFailureMessage)
  httpStatus?: (error: HttpStatusError) => FolderRequestFailureMessage | undefined
}

function recoverFolderRequestFailure(
  recovery: FolderRequestFailureRecovery,
  error: SdkFailure
): FolderRequestFailureMessage | undefined {
  if (isTransportError(error) || isTimeoutError(error)) {
    if (recovery.transient == 'ignore') return null
    return recovery.transient?.(error)
  }

  if (isHttpStatusError(error)) {
    return recovery.httpStatus?.(error)
  }

  return undefined
}

function callSdkMany(
  state: STFolderReadyState,
  requests: readonly FolderRequest[]
): STFolderReadyUpdate {
  return requests.reduce<STFolderReadyUpdate>(
    (update, request) => Update.pipe(update, (nextState) => callSdk(nextState, request)),
    state
  )
}

function isCurrentQueueEvent(state: STFolderReadyState, requestId: string): boolean {
  const item = state.requestQueue.items.find((entry) => entry.id == requestId)
  return item != undefined
}

function defaultSchedule(request: FolderRequest): RequestSchedule {
  const queue =
    request.type == 'taxReturns/pullTaxReturn'
      ? `st-folder:tax-return:${request.taxReturnId}`
      : defaultQueue(request.folderId)

  switch (request.type) {
    case 'folders/setFolderInputs':
      return {
        queue,
        wait: DEBOUNCE_QUEUE_DELAY,
        uniqueKey: 'setFolderInputs'
      }
    default:
      return {
        queue
      }
  }
}

function defaultQueue(folderId: string): string {
  return `st-folder:${folderId}`
}

const DEBOUNCE_QUEUE_DELAY = Duration.millis(2000)
const QUESTIONNAIRE_PROGRESS_WAIT = Duration.millis(1500)
// Single scheduler slot for questionnaire progress writes. Autosave/save-now and
// completion intentionally share this key so completion can cancel stale writes.
const QUESTIONNAIRE_PROGRESS_SLOT_UNIQUE_KEY = 'questionnaire-progress-slot'
